import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import CardSwap, { Card } from "./animatedComponents/CardSwap";

gsap.registerPlugin(ScrollTrigger);

const WhatWeDo = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const waveContainerRef = useRef<HTMLDivElement>(null);
  const wave1Ref = useRef<SVGPathElement>(null);
  const wave2Ref = useRef<SVGPathElement>(null);
  const wave3Ref = useRef<SVGPathElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const imageref = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const decorativeRef1 = useRef<HTMLDivElement>(null);
  const decorativeRef2 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const tl = gsap.timeline();

    // Initial load animations
    tl.from(textRef.current, {
      duration: 0.8,
      x: 50,
      opacity: 0.5,
      ease: "power3.out",
    })
      .from(
        descriptionRef.current,
        {
          duration: 0.6,
          x: 30,
          opacity: 0,
          ease: "power2.out",
        },
        "-=0.4",
      )
      .from(imageref.current, {
        duration: 1,
        y: 50,
        opacity: 1,
        ease: "power3.out",
      });

    // Parallax effects on scroll
    if (waveContainerRef.current) {
      gsap.to(waveContainerRef.current, {
        yPercent: -50,
        ease: "none",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1,
        },
      });
    }

    // Text parallax - slower movement
    if (textRef.current) {
      gsap.to(textRef.current, {
        yPercent: -20,
        ease: "none",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 2,
        },
      });
    }

    // Description parallax - different speed
    if (descriptionRef.current) {
      gsap.to(descriptionRef.current, {
        yPercent: -10,
        ease: "none",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.5,
        },
      });
    }

    // Image parallax - slower movement
    if (imageref.current) {
      gsap.to(imageref.current, {
        yPercent: -10,
        opacity: 1,
        ease: "none",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 2,
        },
      });
    }

    // Decorative elements parallax
    if (decorativeRef1.current) {
      gsap.to(decorativeRef1.current, {
        yPercent: -80,
        xPercent: 20,
        rotation: 45,
        ease: "none",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 0.5,
        },
      });
    }

    if (decorativeRef2.current) {
      gsap.to(decorativeRef2.current, {
        yPercent: -30,
        xPercent: -10,
        rotation: -30,
        ease: "none",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 3,
        },
      });
    }

    // Scroll-triggered text animations
    gsap.to(textRef.current, {
      scale: 1.1,
      opacity: 1,
      scrollTrigger: {
        trigger: textRef.current,
        start: "bottom center",
        end: "bottom 90%",
        scrub: 1,
        toggleActions: "play none none reverse",
      },
    });
    gsap.to(imageref.current, {
      scale: 1,
      opacity: 1,
      scrollTrigger: {
        trigger: imageref.current,
        start: "bottom bottom",
        end: "bottom 90%",
        scrub: 1,
        toggleActions: "play none none reverse",
      },
    });

    // Create continuous wave animations (keeping exact same functionality)
    const createWaveAnimation = (
      path: SVGPathElement,
      duration: number,
      delay: number = 0,
    ) => {
      const pathLength = path.getTotalLength();

      gsap.set(path, {
        strokeDasharray: pathLength,
        strokeDashoffset: pathLength,
      });

      gsap.to(path, {
        strokeDashoffset: 0,
        duration: duration,
        ease: "none",
        repeat: -1,
        delay: delay,
      });
    };

    // Animate waves with different speeds and delays for organic feel (exact same)
    if (wave1Ref.current) createWaveAnimation(wave1Ref.current, 8, 0);
    if (wave2Ref.current) createWaveAnimation(wave2Ref.current, 12, 2);
    if (wave3Ref.current) createWaveAnimation(wave3Ref.current, 10, 4);

    // Create floating animation for the entire wave container (exact same)
    gsap.to(waveContainerRef.current, {
      y: -10,
      duration: 4,
      ease: "sine.inOut",
      repeat: -1,
      yoyo: true,
    });

    // Smooth scroll behavior for any internal links
    const handleSmoothScroll = (e: Event) => {
      const target = e.target as HTMLAnchorElement;
      if (target.hash) {
        e.preventDefault();
        const element = document.querySelector(target.hash);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
    };

    document.addEventListener("click", handleSmoothScroll);

    const cleanupContainer = containerRef.current;
    const animatedElements = [
      containerRef.current,
      waveContainerRef.current,
      wave1Ref.current,
      wave2Ref.current,
      wave3Ref.current,
      textRef.current,
      imageref.current,
      descriptionRef.current,
      decorativeRef1.current,
      decorativeRef2.current,
    ];

    return () => {
      tl.kill();
      ScrollTrigger.getAll().forEach((trigger) => {
        const triggerElement = trigger.trigger;
        if (triggerElement && cleanupContainer?.contains(triggerElement)) {
          trigger.kill();
        }
      });
      gsap.killTweensOf(animatedElements);
      document.removeEventListener("click", handleSmoothScroll);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      id="whatwedo"
      className="relative min-h-screen overflow-hidden bg-[#f5f5f5] mb-10"
    >
      {/* Animated Wave Background */}
      <div ref={waveContainerRef} className="absolute inset-0 opacity-60">
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 1200 800"
          className="absolute inset-0"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <linearGradient
              id="waveGradient1"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop
                offset="0%"
                stopColor="hsl(var(--wave-primary))"
                stopOpacity="0.8"
              />
              <stop
                offset="50%"
                stopColor="hsl(var(--wave-secondary))"
                stopOpacity="0.6"
              />
              <stop
                offset="100%"
                stopColor="hsl(var(--wave-tertiary))"
                stopOpacity="0.4"
              />
            </linearGradient>
            <linearGradient
              id="waveGradient2"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop
                offset="0%"
                stopColor="hsl(var(--wave-secondary))"
                stopOpacity="0.6"
              />
              <stop
                offset="100%"
                stopColor="hsl(var(--wave-primary))"
                stopOpacity="0.8"
              />
            </linearGradient>
            <linearGradient
              id="waveGradient3"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop
                offset="0%"
                stopColor="hsl(var(--wave-tertiary))"
                stopOpacity="0.4"
              />
              <stop
                offset="100%"
                stopColor="hsl(var(--wave-secondary))"
                stopOpacity="0.6"
              />
            </linearGradient>
          </defs>

          {/* Primary Wave */}
          <path
            ref={wave1Ref}
            d="M-200,400 Q200,200 600,350 T1400,300"
            className="wave-path"
            stroke="url(#waveGradient1)"
            strokeWidth="3"
            fill="none"
          />

          {/* Secondary Wave */}
          <path
            ref={wave2Ref}
            d="M-200,500 Q300,250 700,400 T1400,350"
            className="wave-path-secondary"
            stroke="url(#waveGradient2)"
            strokeWidth="2"
            fill="none"
          />

          {/* Tertiary Wave */}
          <path
            ref={wave3Ref}
            d="M-200,600 Q400,350 800,500 T1400,450"
            className="wave-path-tertiary"
            stroke="url(#waveGradient3)"
            strokeWidth="1.5"
            fill="none"
          />

          {/* Additional decorative waves */}
          <path
            d="M-100,300 Q350,100 750,250 T1500,200"
            className="wave-path-tertiary"
            stroke="hsl(var(--wave-primary) / 0.2)"
            strokeWidth="1"
            fill="none"
          >
            <animate
              attributeName="d"
              values="M-100,300 Q350,100 750,250 T1500,200;M-100,320 Q370,120 770,270 T1500,220;M-100,300 Q350,100 750,250 T1500,200"
              dur="6s"
              repeatCount="indefinite"
            />
          </path>
        </svg>
      </div>

      {/* Content */}
      <div className="container px-3 md:px-5 py-10 flex items-center justify-center z-10">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-12 items-center w-full">
          {/* Left Content */}
          <div
            className="text-left px-1 md:px-0 mt-28 sm:mt-32 md:mt-40 lg:mt-64 xl:mt-72"
            data-speed="1.2"
          >
            <h1
              ref={textRef}
              className="font-[600] px-8 text-3xl sm:text-4xl md:text-6xl lg:text-6xl mb-6 font-orbitron leading-tight"
            >
              <span className="text-foreground">What</span>
              <br />
              <span className="text-primary">We Do</span>
            </h1>

            <p
              ref={descriptionRef}
              className="text-justify md:text-left px-4 md:px-0 md:pr-20 text-md md:text-xl mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-poppins text-md text-muted-foreground"
            >
              We provide creative designs, content strategies, and ad scripts,
              along with professional video production, editing, and voiceovers.
              <div className="py-1" />
              Our services also include influencer marketing, modern website
              design, and high-quality edits for photos, banners, and
              billboards, helping your brand stand out everywhere.
            </p>
          </div>

          {/* Right Content */}
          <div
            ref={imageref}
            className="flex justify-center items-center max-h-[300px]"
          >
            <CardSwap
              skewAmount={0}
              cardDistance={60}
              verticalDistance={70}
              delay={5000}
              pauseOnHover={false}
            >
              <Card>
                <img
                  className="rounded-xl"
                  src="/images/whatwedo/p1.jpg"
                  alt="p1"
                />
              </Card>
              <Card>
                <img
                  className="rounded-xl"
                  src="/images/whatwedo/p2.jpg"
                  alt="p2"
                />
              </Card>
            </CardSwap>
          </div>
          {/* <CardGallery /> */}
        </div>
      </div>

      {/* Additional decorative elements with parallax */}
      <div
        ref={decorativeRef1}
        className="absolute bottom-10 right-10 opacity-30"
      >
        <div className="w-32 h-32 border border-accent/20 rounded-full animate-pulse" />
      </div>

      <div
        ref={decorativeRef2}
        className="absolute top-20 right-1/3 opacity-20"
      >
        <div
          className="w-16 h-16 bg-accent/10 rounded-full animate-float-up"
          style={{ animationDelay: "2s" }}
        />
      </div>

      {/* Additional content to demonstrate scroll effects */}
      <div className="absolute hidden lg:block bottom-0 left-1/2 transform -translate-x-1/2 text-center opacity-40 text-primary">
        <div className="animate-bounce">
          <svg
            className="w-6 h-6 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
          <p className="text-sm mt-2 text-primary font-bold">
            Scroll to explore
          </p>
        </div>
      </div>
    </div>
  );
};

export default WhatWeDo;
